import React, { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { MessageCircle, ChevronRight, Home } from 'lucide-react';
import { supabase } from '../supabaseClient';
import ChristmasPillars from '../components/home/ChristmasPillars';
import NotFound from './NotFound';
import { NAVIDAD, formatRD, brandOf, ageOf, prettyName, inCatalog, extraDescription, navidadTitle, navidadWhatsApp } from '../seo/navidadText';

// One page per Christmas kids' bike (/navidad/<slug>/). Description, canonical, Open Graph and
// Product data are written into its static HTML at build time (scripts/inject-metadata.js).
const ChristmasBikeDetail = () => {
    const { slug } = useParams();
    const [bikes, setBikes] = useState(null);

    useEffect(() => {
        supabase.from('bikes_navidad_2025').select('*').then(({ data, error }) => {
            if (error) console.error('Error fetching christmas bikes:', error);
            setBikes(inCatalog(data || []));
        });
    }, []);

    if (bikes === null) {
        return (
            <div className="min-h-screen flex items-center justify-center">
                <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-black"></div>
            </div>
        );
    }

    const bike = bikes.find((b) => b.slug === slug);
    if (!bike) return <NotFound />;

    const name = prettyName(bike);
    const brand = brandOf(bike);
    const related = bikes.filter((b) => b.categoria === bike.categoria && b.slug !== bike.slug).slice(0, 4);
    // The supplier's text, without other shops' names (see navidadText.cleanDescription)
    const extra = extraDescription(bike);

    return (
        <div className="bg-white min-h-screen pt-28 pb-16">
            <Helmet>
                <title>{navidadTitle(bike)}</title>
            </Helmet>

            <div className="max-w-[1600px] mx-auto px-6 sm:px-12 lg:px-16">
                <nav className="flex flex-wrap items-center gap-2 text-xs text-gray-400 font-medium uppercase tracking-wider mb-8">
                    <Link to="/" className="hover:text-black transition-colors flex items-center gap-1"><Home size={13} /> Inicio</Link>
                    <ChevronRight size={13} />
                    <Link to={NAVIDAD.path} className="hover:text-black transition-colors">{NAVIDAD.breadcrumb}</Link>
                    <ChevronRight size={13} />
                    <Link to={`/bicicletas/aro-${String(bike.categoria).replace('.', '-')}`} className="hover:text-black transition-colors">Aro {bike.categoria}</Link>
                    <ChevronRight size={13} />
                    <span className="text-black font-bold">{name}</span>
                </nav>

                <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 lg:gap-16 items-start">
                    <div className="bg-white border border-gray-100 rounded-[2rem] p-8 aspect-square flex items-center justify-center shadow-sm">
                        <img
                            src={bike.imagen_url}
                            alt={`Bicicleta ${name} aro ${bike.categoria} para niños`}
                            className="max-w-full max-h-full object-contain mix-blend-multiply"
                            referrerPolicy="no-referrer"
                        />
                    </div>

                    <div>
                        <div className="flex items-center gap-2 mb-5">
                            <span className="bg-black text-white text-xs font-black uppercase tracking-wider px-3 py-1 rounded-full">Aro {bike.categoria}"</span>
                            <span className="bg-gray-100 text-gray-700 text-xs font-bold uppercase tracking-wider px-3 py-1 rounded-full">{brand}</span>
                            {bike.codigo && <span className="text-[10px] font-mono text-gray-400 bg-gray-50 border border-gray-100 px-2 py-0.5 rounded">{bike.codigo}</span>}
                        </div>

                        <h1 className="text-4xl md:text-5xl font-black italic uppercase tracking-tight text-black leading-none mb-6">
                            {name}
                            <span className="block mt-3 text-lg md:text-xl font-bold normal-case tracking-normal text-gray-500">
                                Bicicleta aro {bike.categoria} para {bike.categoria === '29' ? ageOf(bike) : `niños de ${ageOf(bike)}`}
                            </span>
                        </h1>

                        <p className="text-gray-600 text-lg leading-relaxed mb-8">
                            Bicicleta {brand} aro {bike.categoria}{extra ? `, ${extra.charAt(0).toLowerCase()}${extra.slice(1)}` : ''}. Te la entregamos armada y calibrada en
                            nuestro taller, y puedes apartarla para Nochebuena o el Día de Reyes y retirarla en la tienda de Los Prados.
                        </p>

                        <div className="bg-zinc-50 rounded-2xl p-5 space-y-2 text-sm border border-zinc-200/60 mb-8">
                            <div className="flex justify-between gap-4"><span className="text-gray-500">Edad sugerida</span><strong className="text-black text-right">{ageOf(bike)}</strong></div>
                            <div className="flex justify-between gap-4"><span className="text-gray-500">Marca</span><strong className="text-black">{brand}</strong></div>
                            {bike.modelo && <div className="flex justify-between gap-4"><span className="text-gray-500">Modelo</span><strong className="text-black text-right">{bike.modelo}</strong></div>}
                        </div>

                        <div className="flex flex-col sm:flex-row sm:items-center gap-6 mb-10">
                            <div>
                                <span className="text-[11px] uppercase font-bold text-gray-400 block tracking-widest mb-1">Precio</span>
                                <span className="text-4xl font-black text-black tracking-tight">RD$ {formatRD(bike.precio)}</span>
                            </div>
                            <a
                                href={navidadWhatsApp(bike)}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="inline-flex items-center justify-center gap-2 bg-[#25D366] hover:bg-[#20ba5a] text-white py-4 px-8 rounded-full text-xs font-black uppercase tracking-wider shadow-md transition-transform hover:scale-105"
                            >
                                <MessageCircle size={16} className="fill-current" />
                                <span>Apartar por WhatsApp</span>
                            </a>
                        </div>

                        <p className="text-xs text-gray-400">
                            Lunes a viernes de 9 a. m. a 6 p. m. y sábados de 9 a. m. a 4 p. m. en C/ Eliseo Grullón #26, Los Prados. Tel. (829) 716-3555.
                        </p>
                    </div>
                </div>

                <ChristmasPillars className="mt-16" />

                {related.length > 0 && (
                    <section className="mt-20">
                        <h2 className="text-2xl md:text-3xl font-black italic uppercase tracking-tight mb-8">Más bicicletas aro {bike.categoria}</h2>
                        <div className="grid grid-cols-2 lg:grid-cols-4 gap-6">
                            {related.map((b) => (
                                <Link key={b.slug} to={`/navidad/${b.slug}/`} className="group block">
                                    <div className="bg-white border border-gray-100 rounded-[2rem] p-6 mb-4 aspect-[4/3] flex items-center justify-center transition-all duration-500 shadow-sm group-hover:shadow-xl group-hover:-translate-y-1">
                                        <img src={b.imagen_url} alt={`Bicicleta ${prettyName(b)} aro ${b.categoria}`} className="max-w-full max-h-full object-contain mix-blend-multiply" loading="lazy" referrerPolicy="no-referrer" />
                                    </div>
                                    <div className="text-[11px] font-black uppercase tracking-wider text-red-600">{brandOf(b)}</div>
                                    <h3 className="text-base font-black italic uppercase tracking-tight text-black leading-tight">{prettyName(b)}</h3>
                                    <p className="text-sm font-bold text-black mt-1">RD$ {formatRD(b.precio)}</p>
                                </Link>
                            ))}
                        </div>
                    </section>
                )}

                <div className="mt-16 flex flex-wrap gap-3">
                    <Link to={NAVIDAD.path} className="px-5 py-3 rounded-full border border-gray-200 text-sm font-bold text-gray-700 hover:border-black transition-colors">Todas las bicicletas de Navidad</Link>
                    <Link to={`/bicicletas/aro-${String(bike.categoria).replace('.', '-')}`} className="px-5 py-3 rounded-full border border-gray-200 text-sm font-bold text-gray-700 hover:border-black transition-colors">Todas las aro {bike.categoria}</Link>
                    <Link to="/bicicletas-para-ninos-santo-domingo" className="px-5 py-3 rounded-full border border-gray-200 text-sm font-bold text-gray-700 hover:border-black transition-colors">Cómo elegir la talla</Link>
                </div>
            </div>
        </div>
    );
};

export default ChristmasBikeDetail;
